'use client';

import { Content } from '@prismicio/client';
import { PhaseCarouselCard } from '@/components/redesign/PhaseCarouselCard';
import { useCardCarousel } from '@/hooks/useCardCarousel';

type CarouselSlice = Extract<
  Content.SideHeaderCardsRowSlice,
  { variation: 'carousel' }
>;

type Props = {
  steps: CarouselSlice['primary']['steps'];
};

/**
 * Client-side carousel rail for the `SideHeaderCardsRow` carousel variant.
 * Isolated in its own module so the surrounding slice / shell (`carousel` slice
 * index and `SideHeaderCardsRow` shell) stay on the server-component side of
 * the boundary — importing them into a `'use client'` module trips Next.js's
 * async-client component check.
 */
export function CarouselRail({ steps }: Props) {
  const { carouselRef, scrollEdge, scrollByCard } = useCardCarousel();

  return (
    <div
      ref={carouselRef}
      className="flex w-full overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {/*
        Leading + trailing spacers keep the scroll-snap engine honest at both
        ends and give the last card a little breathing room against the column
        edge — the shell already places this rail inside the 45%/55% split, so
        a minimal spacer is enough.
      */}
      <div aria-hidden className="w-0 shrink-0" />
      {(steps ?? []).map((step, index) => (
        <div
          key={index}
          data-phase-card
          // Widths:
          //   • Mobile / tablet keep the ProcessOverviewThreeStep carousel
          //     sizing (fluid, then `md:w-[496px]`) — the shell is still
          //     stacked here so the right column has the full section width
          //     and 496px fits with a peek.
          //   • From `lg` up the shell switches to flex-row and the header
          //     column takes 70%, so 496px would overflow. `lg:w-[95%]` sizes
          //     the card to the right column instead, leaving a 5% peek of
          //     the next card.
          // `ml-9` (36px) separator between cards mirrors that slice.
          className={`flex w-[92%] shrink-0 flex-col overflow-hidden rounded-[20px] sm:w-[85%] md:w-[496px] md:items-stretch lg:w-[95%] ${
            index === 0 ? '' : 'ml-9'
          }`}
        >
          <PhaseCarouselCard
            icon={step.card_icon}
            title={step.card_title}
            description={step.card_description}
            cta={step.card_cta}
            atStart={scrollEdge.atStart}
            atEnd={scrollEdge.atEnd}
            onPrev={() => scrollByCard(-1)}
            onNext={() => scrollByCard(1)}
            // `h-full` lets the light content block fill its outer wrapper's
            // height. The scroll rail is a flex row with the default
            // `align-items: stretch`, so every card's outer wrapper stretches
            // to the tallest card — without this, shorter cards leave the
            // section background peeking through below their grey block.
            // `lg:w-full` matches the content block width to the outer
            // wrapper at `lg`+ where the wrapper is `lg:w-[95%]` of the
            // narrower right column — otherwise the shared component's
            // fixed `md:w-[496px]` either overflows or leaves a strip on the
            // right, and the outer wrapper's rounded left corners stop
            // reading correctly. Neither override is applied in
            // `ProcessOverviewThreeStep`: that slice pairs the content block
            // with a media panel and keeps the fixed 496px column.
            className="h-full lg:w-full"
          />
        </div>
      ))}
      <div aria-hidden className="w-5 shrink-0" />
    </div>
  );
}
